import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EnhancedTokenManager } from '../../../utils/tokenManager';
import { SubdomainManager } from '../../../utils/subdomainUtils';

interface RouteProtectionProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireOnboarding?: boolean;
  redirectIfAuthenticated?: boolean;
}

interface AuthState {
  loading: boolean;
  isAuthenticated: boolean;
  isOnboarded: boolean;
  hasWorkspace: boolean;
  userProfile: any;
  error?: string;
  checkComplete: boolean;
}

const RouteProtection: React.FC<RouteProtectionProps> = ({
  children,
  requireAuth = false,
  requireOnboarding = false,
  redirectIfAuthenticated = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const checkInProgress = useRef(false);
  const lastCheckPath = useRef<string | null>(null);
  
  const [authState, setAuthState] = useState<AuthState>({
    loading: true,
    isAuthenticated: false,
    isOnboarded: false,
    hasWorkspace: false,
    userProfile: null,
    checkComplete: false,
  });

  useEffect(() => {
    checkAuthenticationStatus();
  }, []);

  useEffect(() => {
    if (checkInProgress.current || lastCheckPath.current === location.pathname) {
      return;
    }
    checkAuthenticationStatus();
  }, [location.pathname]);

  const checkAuthenticationStatus = async () => {
  if (checkInProgress.current) {
    console.log("⏸️ Auth check already in progress, skipping");
    return;
  }

  checkInProgress.current = true;

  try {
    setAuthState(prev => ({ ...prev, loading: true }));
    lastCheckPath.current = location.pathname;

    console.log("🔐 Starting authentication check...");
    console.log("🌐 Current domain:", window.location.hostname);
    console.log("🌐 Is on workspace subdomain:", SubdomainManager.isOnWorkspaceSubdomain());
    console.log("🌐 Environment:", SubdomainManager.getEnvironmentName());

    // ✅ Get tokens BEFORE validation
    const accessTokenBefore = EnhancedTokenManager.getAccessToken();
    const refreshTokenBefore = EnhancedTokenManager.getRefreshToken();
    
    console.log("🔑 Token status BEFORE validation:", {
      hasAccessToken: !!accessTokenBefore,
      hasRefreshToken: !!refreshTokenBefore,
      accessTokenLength: accessTokenBefore?.length || 0
    });

    // ✅ Check and refresh token if needed
    const tokenValid = await EnhancedTokenManager.ensureValidToken();
    
    console.log("🔑 Token validation result:", tokenValid);

    // ✅ Get tokens AFTER validation
    const accessToken = EnhancedTokenManager.getAccessToken();
    const refreshToken = EnhancedTokenManager.getRefreshToken();
    
    console.log("🔑 Token status AFTER validation:", {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      accessTokenLength: accessToken?.length || 0
    });
    
    if (!tokenValid) {
      console.warn("⚠️ Token refresh failed during auth check");

      // Only force a redirect / logout if the tokens were actually cleared
      // (i.e. the server returned 401). A network error leaves the tokens intact
      // so we let the user continue — their next API call will retry the refresh.
      const tokensStillPresent = !!(EnhancedTokenManager.getAccessToken() && EnhancedTokenManager.getRefreshToken());

      if (!tokensStillPresent) {
        const isOnWorkspaceSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
        if (isOnWorkspaceSubdomain && !SubdomainManager.isLocalDevelopment()) {
          console.log("🚫 Tokens gone on workspace subdomain, redirecting to app domain");
          SubdomainManager.navigateToAppDomain('/', true);
          return;
        }
        console.log("⚠️ Tokens gone on app domain, will redirect to login");
      } else {
        console.log("⚠️ Refresh failed but tokens intact (likely network error) — proceeding with existing session");
      }
    }

    const isAuthenticated = !!(accessToken && refreshToken);

    console.log('🔐 Auth check result:', {
      path: location.pathname,
      isAuthenticated,
      hasTokens: !!accessToken,
      tokenRefreshed: tokenValid
    });

    if (!isAuthenticated) {
      console.log("❌ Not authenticated, setting auth state to false");
      
      // ✅ REMOVED: Don't redirect here for workspace subdomains
      // The redirect logic at the bottom will handle it properly
      
      setAuthState({
        loading: false,
        isAuthenticated: false,
        isOnboarded: false,
        hasWorkspace: false,
        userProfile: null,
        checkComplete: true,
        error: 'No tokens found',
      });
      return;
    }

    // ✅ CRITICAL: Try stored profile first - this is the fast path
    let userProfile = EnhancedTokenManager.getUserProfile();
    
    if (userProfile && userProfile.onboarded !== undefined) {
      const hasWorkspace = !!(userProfile.workspace_id || userProfile.workspace?.id);
      
      console.log("✅ Using cached user profile:", {
        onboarded: userProfile.onboarded,
        hasWorkspace,
        workspaceId: userProfile.workspace_id
      });
      
      setAuthState({
        loading: false,
        isAuthenticated: true,
        isOnboarded: userProfile.onboarded,
        hasWorkspace,
        userProfile,
        checkComplete: true,
      });
      return;
    }

    // ✅ Try JWT fallback - decode token to check workspace
    const payload = EnhancedTokenManager.decodeJWT(accessToken);
    if (payload && payload.workspace_id && payload.workspace_id > 0) {
      console.log("✅ Found workspace_id in JWT:", payload.workspace_id);
      
      const userId = EnhancedTokenManager.getCurrentUserId();
      if (userId) {
        EnhancedTokenManager.setWorkspaceIdForUser(userId, payload.workspace_id);
      }

      setAuthState({
        loading: false,
        isAuthenticated: true,
        isOnboarded: true,
        hasWorkspace: true,
        userProfile: null,
        checkComplete: true,
      });
      return;
    }

    // ✅ LAST RESORT: Allow UI to render while API call happens in background
    console.log("⚠️ No cached profile or JWT workspace, allowing render and fetching in background");
    
    setAuthState({
      loading: false,
      isAuthenticated: true,
      isOnboarded: false,
      hasWorkspace: false,
      userProfile: null,
      checkComplete: true,
    });

    // Background API call to get fresh profile
    EnhancedTokenManager.fetchAndCheckUserProfile(accessToken)
      .then(profileResult => {
        if (profileResult.success && profileResult.profile) {
          const profile = profileResult.profile;
          const hasWorkspace = !!(profile.workspace_id || profile.workspace?.id);
          
          console.log("✅ Background profile fetch successful:", {
            onboarded: profile.onboarded,
            hasWorkspace
          });
          
          setAuthState({
            loading: false,
            isAuthenticated: true,
            isOnboarded: profile.onboarded,
            hasWorkspace,
            userProfile: profile,
            checkComplete: true,
          });
        } else {
          console.warn("⚠️ Background profile fetch failed:", profileResult);
          
          // ✅ FIX: Only redirect to app domain if on workspace subdomain
          const isOnWorkspaceSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
          if (isOnWorkspaceSubdomain && !SubdomainManager.isLocalDevelopment()) {
            console.log("🚫 Profile fetch failed on workspace subdomain, redirecting to app domain");
            SubdomainManager.navigateToAppDomain('/', true);
          }
        }
      })
      .catch(err => {
        console.error('❌ Background profile fetch error:', err);
        
        // ✅ FIX: Only redirect to app domain if on workspace subdomain
        const isOnWorkspaceSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
        if (isOnWorkspaceSubdomain && !SubdomainManager.isLocalDevelopment()) {
          console.log("🚫 Profile fetch error on workspace subdomain, redirecting to app domain");
          SubdomainManager.navigateToAppDomain('/', true);
        }
      });

  } catch (error) {
    console.error('❌ Error checking authentication status:', error);
    
    // ✅ FIX: Only redirect to app domain if on workspace subdomain
    const isOnWorkspaceSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
    if (isOnWorkspaceSubdomain && !SubdomainManager.isLocalDevelopment()) {
      console.log("🚫 Auth check error on workspace subdomain, redirecting to app domain");
      SubdomainManager.navigateToAppDomain('/', true);
      return;
    }

    setAuthState({
      loading: false,
      isAuthenticated: false,
      isOnboarded: false,
      hasWorkspace: false,
      userProfile: null,
      checkComplete: true,
      error: 'Authentication check failed',
    });
  } finally {
    checkInProgress.current = false;
  }
};

  // Apply redirects AFTER check is complete
  useEffect(() => {
    if (!authState.checkComplete || authState.loading) {
      return;
    }

    console.log("🔄 Applying redirects based on auth state:", {
      redirectIfAuthenticated,
      requireAuth,
      requireOnboarding,
      isAuthenticated: authState.isAuthenticated,
      isOnboarded: authState.isOnboarded,
      currentPath: location.pathname
    });

    if (redirectIfAuthenticated && authState.isAuthenticated) {
      console.log("↪️ Redirecting authenticated user away from auth page");
      if (authState.isOnboarded) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
      return;
    }

    if (requireAuth && !authState.isAuthenticated) {
      console.log("↪️ Redirecting unauthenticated user to login");
      navigate('/login', { replace: true });
      return;
    }

    if (requireOnboarding && authState.isAuthenticated && !authState.isOnboarded) {
      console.log("↪️ Redirecting non-onboarded user");
      navigate('/onboarding', { replace: true });
      return;
    }

    if (authState.isAuthenticated && authState.isOnboarded && location.pathname === '/onboarding') {
      console.log("↪️ Redirecting onboarded user away from onboarding");
      navigate('/dashboard', { replace: true });
      return;
    }

  }, [authState.checkComplete, authState.isAuthenticated, authState.isOnboarded, authState.loading]);

  return <>{children}</>;
};

export const WorkspaceSubdomainGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const checkSubdomain = async () => {
      if (SubdomainManager.isLocalDevelopment()) {
        setShouldRender(true);
        return;
      }

      // Ensure token is valid before subdomain check
      const tokenValid = await EnhancedTokenManager.ensureValidToken();
      
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken || !tokenValid) {
        // ✅ FIX: Only redirect if on workspace subdomain
        const isOnWorkspaceSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
        if (isOnWorkspaceSubdomain) {
          console.log("🚫 No valid token for workspace subdomain guard, redirecting to app domain");
          SubdomainManager.navigateToAppDomain('/', true);
          return;
        }
      }

      const userProfile = EnhancedTokenManager.getUserProfile();
      if (!userProfile || !userProfile.onboarded) {
        setShouldRender(true);
        return;
      }

      const storedSubdomain = SubdomainManager.getStoredWorkspaceSubdomain();
      const currentSubdomain = SubdomainManager.getCurrentSubdomain();

      if (storedSubdomain && currentSubdomain !== storedSubdomain) {
        if (SubdomainManager.isOnWorkspaceSubdomain() && currentSubdomain) {
          // Already on a workspace subdomain — stored value is stale. Update and render
          // instead of bouncing the user away (which causes a blank page on hard refresh).
          localStorage.setItem('workspace_subdomain', currentSubdomain);
          setShouldRender(true);
          return;
        }
        console.log(`Redirecting to workspace subdomain: ${storedSubdomain}`);
        SubdomainManager.navigateToSubdomain(storedSubdomain, window.location.pathname);
        return;
      }

      setShouldRender(true);
    };

    checkSubdomain();
  }, []);

  return shouldRender ? <>{children}</> : null;
};

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [authCheck, setAuthCheck] = useState<{
    isAuthenticated: boolean | null;
    needsOnboarding: boolean | null;
    isChecking: boolean;
  }>({
    isAuthenticated: null,
    needsOnboarding: null,
    isChecking: true
  });

  useEffect(() => {
    const checkAuth = async () => {
      // Ensure token is valid first
      const tokenValid = await EnhancedTokenManager.ensureValidToken();
      
      const accessToken = EnhancedTokenManager.getAccessToken();
      
      if (!accessToken || !tokenValid) {
        console.log("No valid access token in ProtectedRoute, redirecting");
        
        // ✅ FIX: Only redirect to app domain if on workspace subdomain
        const isOnWorkspaceSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
        if (isOnWorkspaceSubdomain && !SubdomainManager.isLocalDevelopment()) {
          SubdomainManager.navigateToAppDomain('/', true);
        } else {
          navigate('/login', { replace: true });
        }
        return;
      }

      // Check stored profile first
      const userProfile = EnhancedTokenManager.getUserProfile();
      if (userProfile && userProfile.onboarded !== undefined) {
        setAuthCheck({
          isAuthenticated: true,
          needsOnboarding: !userProfile.onboarded,
          isChecking: false
        });
        
        if (!userProfile.onboarded) {
          navigate('/onboarding', { replace: true });
        }
        return;
      }

      // JWT fallback
      const payload = EnhancedTokenManager.decodeJWT(accessToken);
      if (payload && payload.workspace_id && payload.workspace_id > 0) {
        setAuthCheck({
          isAuthenticated: true,
          needsOnboarding: false,
          isChecking: false
        });
        return;
      }

      // Allow render and fetch in background
      setAuthCheck({
        isAuthenticated: true,
        needsOnboarding: false,
        isChecking: false
      });

      // Background API check
      EnhancedTokenManager.fetchAndCheckUserProfile(accessToken)
        .then(result => {
          if (result.success && result.profile && !result.profile.onboarded) {
            navigate('/onboarding', { replace: true });
          } else if (!result.success) {
            // ✅ FIX: Only redirect to app domain if on workspace subdomain
            const isOnWorkspaceSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
            if (isOnWorkspaceSubdomain && !SubdomainManager.isLocalDevelopment()) {
              SubdomainManager.navigateToAppDomain('/', true);
            }
          }
        });
    };

    checkAuth();
  }, [navigate]);

  return authCheck.isAuthenticated ? (
    <WorkspaceSubdomainGuard>{children}</WorkspaceSubdomainGuard>
  ) : null;
};

export const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RouteProtection redirectIfAuthenticated={true}>
    {children}
  </RouteProtection>
);

export const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      // Ensure token is valid first
      const tokenValid = await EnhancedTokenManager.ensureValidToken();
      
      const accessToken = EnhancedTokenManager.getAccessToken();
      
      if (!accessToken || !tokenValid) {
        // ✅ FIX: Only redirect to app domain if on workspace subdomain
        const isOnWorkspaceSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
        if (isOnWorkspaceSubdomain && !SubdomainManager.isLocalDevelopment()) {
          SubdomainManager.navigateToAppDomain('/', true);
        } else {
          navigate('/login', { replace: true });
        }
        return;
      }

      // Check stored profile
      const userProfile = EnhancedTokenManager.getUserProfile();
      if (userProfile && userProfile.onboarded) {
        navigate('/dashboard', { replace: true });
        return;
      }

      setIsChecking(false);
    };

    checkAuth();
  }, [navigate]);

  return <>{children}</>;
};

export default RouteProtection;